import React, { useEffect, useMemo, useRef } from 'react';

import { formatNumber } from '../../services/csvDataService';
import { buildPlayerRoundRowKey } from '../../utils/playerStatsCharts';

const PARTY_LABELS = ['P1', 'P2', 'P3', 'P4'];
const PARTY_KEYS = ['quilles1', 'quilles2', 'quilles3', 'quilles4'];

function maxPlayerPartySlots(rounds) {
	let slots = 0;
	rounds.forEach((round) => {
		if (Number(round.quilles1) > 0) slots = Math.max(slots, 1);
		if (Number(round.quilles2) > 0) slots = Math.max(slots, 2);
		if (Number(round.quilles3) > 0) slots = Math.max(slots, 3);
		if (Number(round.quilles4) > 0) slots = Math.max(slots, 4);
	});
	return slots || 3;
}

function isPartyCellHighlighted(rowKey, partyIndex, highlightedCells) {
	if (!highlightedCells?.length) return false;
	return highlightedCells.some(
		(cell) => cell.rowKey === rowKey && cell.partyIndex === partyIndex,
	);
}

function isRowHighlighted(rowKey, highlightedCells) {
	if (!highlightedCells?.length) return false;
	return highlightedCells.some((cell) => cell.rowKey === rowKey);
}

function compareRoundsChronological(a, b) {
	if (a.seasonYear !== b.seasonYear) return a.seasonYear - b.seasonYear;
	const byRound = a.round.localeCompare(b.round, undefined, { numeric: true });
	if (byRound !== 0) return byRound;
	const byChamp = a.championship.localeCompare(b.championship, 'fr');
	if (byChamp !== 0) return byChamp;
	return a.category.localeCompare(b.category, 'fr');
}

function compareRoundsDescending(a, b) {
	if (b.seasonYear !== a.seasonYear) return b.seasonYear - a.seasonYear;
	const byRound = b.round.localeCompare(a.round, undefined, { numeric: true });
	if (byRound !== 0) return byRound;
	const byChamp = a.championship.localeCompare(b.championship, 'fr');
	if (byChamp !== 0) return byChamp;
	return a.category.localeCompare(b.category, 'fr');
}

export default function PlayerRoundsTable({
	rounds,
	totalParties,
	totalQuilles,
	showSeasonYear = false,
	scopeLabel = 'saison',
	highlightedCells = null,
}) {
	const rowRefs = useRef(new Map());
	const displayRounds = useMemo(
		() => (showSeasonYear
			? [...rounds].sort(compareRoundsDescending)
			: rounds),
		[rounds, showSeasonYear],
	);
	const cumulByRowKey = useMemo(() => {
		const chronological = showSeasonYear
			? [...rounds].sort(compareRoundsChronological)
			: rounds;
		const map = new Map();
		let cumul = 0;
		chronological.forEach((round) => {
			cumul += Number(round.quilles);
			map.set(buildPlayerRoundRowKey(round), cumul);
		});
		return map;
	}, [rounds, showSeasonYear]);
	const partySlots = useMemo(() => maxPlayerPartySlots(rounds), [rounds]);
	const footerPartyTotals = useMemo(
		() => PARTY_KEYS.slice(0, partySlots).map((key) => (
			rounds.reduce((sum, round) => sum + Number(round[key] ?? 0), 0)
		)),
		[rounds, partySlots],
	);
	const footerAverage = useMemo(() => {
		if (totalParties > 0) return totalQuilles / totalParties;
		let sum = 0;
		let count = 0;
		rounds.forEach((round) => {
			PARTY_KEYS.slice(0, partySlots).forEach((key) => {
				const n = Number(round[key]);
				if (n > 0) {
					sum += n;
					count += 1;
				}
			});
		});
		return count > 0 ? sum / count : 0;
	}, [rounds, partySlots, totalParties, totalQuilles]);

	const focusRowKey = highlightedCells?.[0]?.rowKey ?? null;

	useEffect(() => {
		if (!focusRowKey) return;
		const row = rowRefs.current.get(focusRowKey);
		row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [focusRowKey, highlightedCells]);

	if (!rounds.length) return null;

	return (
		<div className="new-results-player-rounds-summary">
			<p className="new-results-hint new-results-hint--inline">
				Manches — scores par partie (P1 à P{partySlots})
				{partySlots === 4 ? ', compétition en 4 parties' : ''}
				{showSeasonYear ? ' · toutes saisons' : ''}
			</p>
			<div className="new-results-table-scroll new-results-table-scroll--detail">
				<table className="new-results-table new-results-table--detail new-results-table--player-rounds">
					<thead>
						<tr>
							{showSeasonYear && <th className="new-results-num">Saison</th>}
							<th className="new-results-num">Manche</th>
							<th>Équipe</th>
							{PARTY_LABELS.slice(0, partySlots).map((label) => (
								<th key={label} className="new-results-num">{label}</th>
							))}
							<th className="new-results-num">Total</th>
							<th className="new-results-num">MOYENNE</th>
							<th className="new-results-num">Cumul</th>
						</tr>
					</thead>
					<tbody>
						{displayRounds.map((round) => {
							const rowKey = buildPlayerRoundRowKey(round);
							const cumul = cumulByRowKey.get(rowKey) ?? Number(round.quilles);
							const rowHighlighted = isRowHighlighted(rowKey, highlightedCells);
							return (
								<tr
									key={rowKey}
									ref={(node) => {
										if (node) rowRefs.current.set(rowKey, node);
										else rowRefs.current.delete(rowKey);
									}}
									className={rowHighlighted ? 'new-results-player-rounds-row--highlight' : undefined}
								>
									{showSeasonYear && (
										<td className="new-results-num">{round.seasonYear ?? '—'}</td>
									)}
									<td className="new-results-num">{Number(round.round)}</td>
									<td className="new-results-player-rounds-team">
										<span className="new-results-championship-main">{round.teamName}</span>
										{round.category && (
											<span className="new-results-championship-category">{round.category}</span>
										)}
									</td>
									{PARTY_KEYS.slice(0, partySlots).map((key, partyIndex) => (
										<td
											key={key}
											className={`new-results-num${isPartyCellHighlighted(rowKey, partyIndex + 1, highlightedCells)
												? ' new-results-player-rounds-cell--highlight'
												: ''}`}
										>
											{Number(round[key]) > 0 ? formatNumber(round[key]) : '—'}
										</td>
									))}
									<td className="new-results-num new-results-num--highlight">
										<strong>{formatNumber(round.quilles)}</strong>
									</td>
									<td className="new-results-num">{formatNumber(round.average, 2)}</td>
									<td className="new-results-num">{formatNumber(cumul)}</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={showSeasonYear ? 3 : 2}><strong>Total</strong></td>
							{footerPartyTotals.map((value, index) => (
								<td key={PARTY_KEYS[index]} className="new-results-num">
									<strong>{formatNumber(value)}</strong>
								</td>
							))}
							<td className="new-results-num"><strong>{formatNumber(totalQuilles)}</strong></td>
							<td className="new-results-num"><strong>{formatNumber(footerAverage, 2)}</strong></td>
							<td className="new-results-num"><strong>{formatNumber(totalQuilles)}</strong></td>
						</tr>
						<tr className="new-results-summary-foot-meta">
							<td colSpan={partySlots + (showSeasonYear ? 6 : 5)}>
								{formatNumber(totalParties)} parties sur la {scopeLabel}
								{partySlots === 4 ? ' · parties en 4 jeux (P4)' : ''}
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
}
